import { useEffect, useState } from 'react'

import type { ApiResult, Resource } from './client.ts'

// One read, owned as its own state. Extracted from the webhooks data hooks when
// the credentials screen became the second consumer.
//
// Screens compose several of these rather than one joined load so the reads
// fail independently: a dashboard that blanks one card because an unrelated
// query timed out is worse than one that shows the card with that data missing.

export const useApiResource = <T,>(
  load: (signal: AbortSignal) => Promise<ApiResult<T>>,
  deps: readonly unknown[]
): Resource<T> => {
  const [resource, setResource] = useState<Resource<T>>({ status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    setResource({ status: 'loading' })

    void (async () => {
      try {
        const result = await load(controller.signal)
        if (controller.signal.aborted) return
        setResource(
          result.ok
            ? { status: 'ready', data: result.data }
            : { status: 'failed', failure: result.failure }
        )
      } catch {
        // Only an abort reaches here; a superseded render has nothing to report.
      }
    })()

    return () => controller.abort()
    // `load` is re-created per render by design — the caller's deps are the real
    // identity of the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return resource
}
